import { useId, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { addRecipients, isValidEmail, parseRecipientInput } from '../../lib/mailCompose';

/**
 * Recipient chips. Enter, comma, semicolon or blur commits the typed text, paste splits on separators,
 * Backspace on an empty input removes the last chip. Invalid entries stay as flagged chips so the user
 * can fix or remove them; sending is blocked until they are gone.
 */
export function RecipientField({
  label,
  value,
  pending,
  onChange,
  onPendingChange,
  autoFocus,
}: {
  label: string;
  value: string[];
  /** Text typed but not yet committed to a chip. */
  pending: string;
  onChange: (next: string[]) => void;
  onPendingChange: (text: string) => void;
  autoFocus?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();

  const commit = (raw: string) => {
    const entries = parseRecipientInput(raw);
    if (entries.length) onChange(addRecipients(value, entries));
    onPendingChange('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      commit(pending);
    } else if (e.key === 'Backspace' && pending === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    e.preventDefault();
    commit(`${pending} ${text}`);
  };

  return (
    <div className="mail-recipients" onClick={() => inputRef.current?.focus()}>
      <label className="mail-recipients-label" htmlFor={id}>
        {label}
      </label>
      <ul className="mail-chips" aria-label={`${label} recipients`}>
        {value.map((entry) => {
          const valid = isValidEmail(entry);
          return (
            <li key={entry} className={valid ? 'mail-chip' : 'mail-chip mail-chip-invalid'}>
              <span title={valid ? entry : `"${entry}" is not a valid email address`}>{entry}</span>
              {!valid && <span className="mail-sr"> (invalid address)</span>}
              <button
                type="button"
                className="mail-chip-remove"
                aria-label={`Remove ${entry}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(value.filter((v) => v !== entry));
                }}
              >
                <X size={12} aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>
      <input
        id={id}
        ref={inputRef}
        type="text"
        className="mail-recipients-input"
        autoComplete="off"
        spellCheck={false}
        autoFocus={autoFocus}
        value={pending}
        onChange={(e) => {
          const text = e.target.value;
          // A separator typed by IME / mobile keyboards arrives in onChange rather than keydown.
          if (/[,;]$/.test(text)) commit(text);
          else onPendingChange(text);
        }}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => pending.trim() && commit(pending)}
      />
    </div>
  );
}
